import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, PlayCircle, Sparkles,
  Stethoscope, Scissors, Dumbbell, UtensilsCrossed, Briefcase, Users, Wrench, Landmark,
  Wand2, Compass, Zap, Target, Lock, Rocket, ShieldCheck,
} from "lucide-react";

import ExecHeroDashboard from "./landing/ExecHeroDashboard";
import ExecProblemCanvas from "./landing/ExecProblemCanvas";
import ExecAICore from "./landing/ExecAICore";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" } };

export default function Landing() {
  return (
    <div className="exec-scope min-h-screen overflow-x-hidden">
      <Nav />

      {/* SECTION 1 — HERO */}
      <section className="relative pt-24 lg:pt-28 pb-16 lg:pb-24">
        <div className="absolute inset-0 exec-grid-bg pointer-events-none" />
        <div className="relative mx-auto max-w-[1280px] px-6 lg:px-10 grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <div className="exec-eyebrow mb-6">Executive AI for modern businesses</div>
            <h1 className="exec-h1 font-display">
              Executive AI<br/>for businesses<br/>built to <span className="exec-gold-word">scale.</span>
            </h1>
            <p className="exec-body mt-7 max-w-[52ch]">
              Built for founders and business owners who want to scale without operational complexity.
              VELQUE quietly observes operations, prepares priorities before the day begins,
              automates repetitive work, and lets you focus on growth instead of daily management.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/signup" className="exec-btn-primary" data-testid="hero-explore">
                Explore VELQUE
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <a href="#solution" className="exec-btn-ghost" data-testid="hero-demo">
                <PlayCircle className="w-4 h-4" strokeWidth={1.5} />
                Watch Demo
              </a>
            </div>

            {/* trust row */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12.5px] text-exec-mute">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-exec-emerald" strokeWidth={1.5}/> Automate Daily Operations</span>
              <span className="inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-exec-gold" strokeWidth={1.5}/> AI-powered by default</span>
              <span className="inline-flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-exec-emerald" strokeWidth={1.5}/> Save Time Every Day</span>
            </div>
          </motion.div>

          {/* Right — Executive dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <ExecHeroDashboard />
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — TRUSTED BY */}
      <section className="py-14 border-y border-white/[0.05] bg-black/10">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
          <div className="text-center exec-eyebrow mb-8" data-testid="trusted-eyebrow">
            BUILT FOR MODERN SERVICE BUSINESSES
          </div>
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.6, staggerChildren: 0.05 }}
            className="flex flex-wrap items-center justify-center gap-3"
          >
            {[
              { i: Stethoscope, l: "Clinics" }, { i: Scissors, l: "Salons" },
              { i: Dumbbell, l: "Gyms" }, { i: UtensilsCrossed, l: "Restaurants" },
              { i: Briefcase, l: "Agencies" }, { i: Users, l: "Consultants" },
              { i: Wrench, l: "Repair Shops" }, { i: Landmark, l: "Professional Services" },
            ].map(({ i: Icon, l }) => (
              <div key={l} className="exec-chip">
                <Icon className="w-4 h-4 text-exec-emerald" strokeWidth={1.4} />
                {l}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 3 — THE PROBLEM */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10 grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }}>
            <div className="exec-divider-badge mb-6">The Problem</div>
            <h2 className="exec-h2 font-display">
              Running a business is hard.<br/>
              Operations make it <span className="italic text-exec-gold-2">harder.</span>
            </h2>
            <p className="exec-body mt-7 max-w-[46ch]">
              Business owners juggle calls, appointments, vendors, payments, invoices, tasks,
              and a thousand small things that consume time and energy.
            </p>
            <p className="exec-body mt-4 max-w-[46ch] text-exec-mute">
              Important things get missed. Opportunities are lost. Growth slows down —
              not because the business isn&apos;t working, but because <em>the operations aren&apos;t.</em>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <ExecProblemCanvas />
          </motion.div>
        </div>
      </section>

      {/* SECTION 4 — THE SOLUTION */}
      <section id="solution" className="relative py-24 lg:py-32 border-y border-white/[0.05]">
        <div className="absolute inset-0 exec-grid-bg pointer-events-none" />
        <div className="relative mx-auto max-w-[1280px] px-6 lg:px-10 grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-14 lg:gap-16 items-center">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }}>
            <div className="exec-divider-badge mb-6">The Solution</div>
            <h2 className="exec-h2 font-display">
              VELQUE works<br/>
              <span className="exec-gold-word">before</span> you do.
            </h2>
            <p className="exec-body mt-7 max-w-[48ch]">
              One Executive AI that coordinates every operational responsibility inside your business —
              silently, in the background, all day.
            </p>
            <p className="exec-body mt-4 max-w-[48ch] text-exec-mute">
              By the time you sit down each morning, priorities are prepared, follow-ups are drafted,
              vendors are managed, and appointments are confirmed. Not a stack of tools.
              One Executive.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1 }}
          >
            <ExecAICore />
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 — OUTCOMES */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="exec-divider-badge mb-5 mx-auto">Business Outcomes</div>
            <h2 className="exec-h2 font-display max-w-[24ch] mx-auto">
              Every day begins <span className="exec-gold-word">prepared.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {OUTCOMES.map((o, i) => (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: 0.06 * i }}
                className="exec-card p-7"
                data-testid={`outcome-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-exec-emerald-3/40 border border-exec-emerald/25 flex items-center justify-center mb-5">
                  <o.icon className="w-5 h-5 text-exec-emerald-2" strokeWidth={1.5} />
                </div>
                <h3 className="exec-h3 font-display">{o.title}</h3>
                <p className="exec-body mt-2.5 text-[14.5px]">{o.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — FINAL CTA */}
      <section className="relative py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0" style={{
            background: "radial-gradient(700px 400px at 50% 40%, rgba(47,203,169,0.14), transparent 65%)"
          }} />
        </div>
        <div className="relative mx-auto max-w-[1000px] px-6 lg:px-10 text-center">
          <motion.h2 {...fadeUp} transition={{ duration: 0.8 }} className="exec-h2 font-display">
            Start every business day <span className="exec-gold-word">prepared.</span>
          </motion.h2>
          <motion.p
            {...fadeUp} transition={{ duration: 0.8, delay: 0.1 }}
            className="exec-body mt-8 max-w-[62ch] mx-auto text-[17px]"
          >
            Your business already generates thousands of operational decisions every month.
            VELQUE ensures the right work reaches the right people
            before it becomes a problem.
          </motion.p>
          <motion.div
            {...fadeUp} transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <Link to="/signup" className="exec-btn-primary" data-testid="cta-start">
              Start Free
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <a href="#solution" className="exec-btn-ghost" data-testid="cta-demo">
              <PlayCircle className="w-4 h-4" strokeWidth={1.5} />
              Watch Demo
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* -------------------- Nav -------------------- */
function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-40">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10 pt-5">
        <div className="flex items-center justify-between h-14 px-4 rounded-full border border-white/[0.06]"
             style={{ background: "rgba(11,37,41,0.72)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }}>
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <span className="w-1 h-5 rounded-full bg-exec-gold" />
           <span className="font-display text-[30px] tracking-tight text-[#D4A017]">
              VELQUE
           </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-flex px-4 py-2 text-[18.5px] text-exec-ink-2 hover:text-white transition-colors" data-testid="nav-login">
              Sign in
            </Link>
            <Link to="/signup" className="inline-flex items-center gap-1.5 h-10 pl-4 pr-3.5 rounded-full bg-exec-gold text-[#0B2529] text-[13px] font-semibold hover:brightness-105 transition-all" data-testid="nav-get-started">
              Get Started <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.2} />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

/* -------------------- Footer -------------------- */
function Footer() {
  return (
    <footer className="border-t border-white/[0.06] py-16" data-testid="landing-footer">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-1 h-5 rounded-full bg-exec-gold" />
              <span className="font-display text-[17px]">Velque<span className="text-exec-mute"></span></span>
            </div>
            <p className="text-[13.5px] text-exec-mute max-w-[42ch] leading-relaxed italic">
              &ldquo;Preparation creates better decisions.<br/>Executive AI works before you do.&rdquo;
            </p>
          </div>
          <div className="text-[11.5px] tracking-[0.24em] uppercase text-exec-mute-2">
            © MMXXVI · VELQUE
          </div>
        </div>
      </div>
    </footer>
  );
}

/* -------------------- OUTCOMES data -------------------- */
const OUTCOMES = [
  { icon: Wand2,   title: "Works Before You Do",         body: "Executive AI prepares priorities and follow-ups before the business day begins." },
  { icon: Compass, title: "Less Management. More Momentum.", body: "Reduce operational friction. Move faster without the overhead." },
  { icon: Zap,     title: "Every Day Begins Prepared",   body: "Know exactly what matters — nothing important is discovered late." },
  { icon: Target,  title: "Never Miss Opportunities",    body: "Calls, payments, appointments and follow-ups always stay organised." },
  { icon: Rocket,  title: "Scale Without Chaos",         body: "Grow revenue without adding management overhead or new tools." },
  { icon: ShieldCheck, title: "Enterprise Ready",         body: "Reliable, secure and built for the operational demands of a growing business." },
];
